import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Pill } from '../shared/ui';
import * as notifApi from '../api/platform-notifications.api.js';

const severityTone = (sev) =>
  ({ critical: 'red', warning: 'amber', success: 'green', info: 'blue' })[sev] || 'blue';

const severityIcon = (sev) =>
  ({ critical: 'AlertOctagon', warning: 'AlertTriangle', success: 'CheckCircle2', info: 'Info' })[sev] || 'Bell';

const fmt = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return String(ts);
  const diff = Math.floor((Date.now() - d) / 60000);
  if (diff < 1)    return 'just now';
  if (diff < 60)   return diff + 'm ago';
  if (diff < 1440) return Math.floor(diff / 60) + 'h ago';
  return d.toLocaleDateString();
};

export default function NotificationCenter() {
  const [open, setOpen]             = useState(false);
  const [notifications, setNotifs]  = useState([]);
  const [loading, setLoading]       = useState(false);
  const ref                         = useRef(null);
  const navigate                    = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    notifApi.list()
      .then(data => setNotifs(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const unread = notifications.filter(n => !n.read).length;

  const markRead = useCallback(async (id) => {
    setNotifs(ns => ns.map(n => n.id === id ? { ...n, read: true } : n));
    await notifApi.markRead(id).catch(() => {});
  }, []);

  const markAll = useCallback(async () => {
    setNotifs(ns => ns.map(n => ({ ...n, read: true })));
    await notifApi.markAll().catch(() => {});
  }, []);

  const dismiss = useCallback(async (id) => {
    setNotifs(ns => ns.filter(n => n.id !== id));
    await notifApi.dismiss(id).catch(() => {});
  }, []);

  const goTo = (href, id) => {
    markRead(id);
    setOpen(false);
    navigate(href || '/platform/audit');
  };

  return (
    <div className="nx-notif-wrap" ref={ref}>
      <button
        className={'nx-notif-btn' + (open ? ' is-open' : '')}
        onClick={() => { setOpen(v => !v); }}
        title="Notifications"
      >
        <NexusIcon name="Bell" size={16} />
        {unread > 0 && <span className="nx-notif-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className="nx-notif-panel">
          <div className="nx-notif-header">
            <span className="nx-notif-title">
              Notifications {unread > 0 && <Pill tone="amber">{unread} unread</Pill>}
            </span>
            {unread > 0 && (
              <button className="nx-panel-link" onClick={markAll}>Mark all read</button>
            )}
          </div>

          <div className="nx-notif-list">
            {loading && (
              <div className="nx-notif-empty">
                <NexusIcon name="Loader" size={20} style={{ opacity: 0.4 }} />
                <span>Loading…</span>
              </div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="nx-notif-empty">
                <NexusIcon name="BellOff" size={24} style={{ opacity: 0.3 }} />
                <span>No notifications</span>
              </div>
            )}
            {!loading && notifications.map(n => (
              <div
                key={n.id}
                className={'nx-notif-item' + (!n.read ? ' is-unread' : '')}
                onClick={() => goTo(n.metadata?.href, n.id)}
              >
                <span className={'nx-notif-icon tone-' + severityTone(n.severity)}>
                  <NexusIcon name={severityIcon(n.severity)} size={14} />
                </span>
                <div className="nx-notif-body">
                  <span className="nx-notif-item-title">{n.title}</span>
                  <span className="nx-notif-item-body">{n.body}</span>
                  {n.createdAt && <span className="nx-notif-time">{fmt(n.createdAt)}</span>}
                </div>
                <button
                  className="nx-notif-dismiss"
                  title="Dismiss"
                  onClick={e => { e.stopPropagation(); dismiss(n.id); }}
                >
                  <NexusIcon name="X" size={12} />
                </button>
              </div>
            ))}
          </div>

          <div className="nx-notif-footer">
            <button className="nx-panel-link" onClick={() => { setOpen(false); navigate('/platform/audit'); }}>
              View audit log <span aria-hidden>→</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
